<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import BaileysCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/BaileysCampaign/BaileysCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';

const { t } = useI18n();
const getters = useStoreGetters();

const selectedCampaign = ref(null);
const [showBaileysCampaignDialog, toggleBaileysCampaignDialog] = useToggle();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);
const confirmDeleteCampaignDialogRef = ref(null);

const allCampaigns = computed(() => getters['campaigns/getAllCampaigns'].value);

const baileysWACampaigns = computed(() =>
  (allCampaigns.value || []).filter(
    c =>
      c.campaign_type === 'one_off' &&
      c.inbox?.channel_type === 'Channel::Whatsapp' &&
      c.inbox?.provider === 'baileys'
  )
);

const hasNoCampaigns = computed(
  () => baileysWACampaigns.value.length === 0 && !isFetchingCampaigns.value
);

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};
</script>

<template>
  <CampaignLayout
    :header-title="t('BAILEYS_CAMPAIGN.HEADER_TITLE')"
    :button-label="t('BAILEYS_CAMPAIGN.NEW_CAMPAIGN')"
    @click="toggleBaileysCampaignDialog()"
    @close="toggleBaileysCampaignDialog(false)"
  >
    <template #action>
      <BaileysCampaignDialog
        v-if="showBaileysCampaignDialog"
        @close="toggleBaileysCampaignDialog(false)"
      />
    </template>

    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <CampaignList
      v-else-if="!hasNoCampaigns"
      :campaigns="baileysWACampaigns"
      @delete="handleDelete"
    />

    <div
      v-else
      class="flex flex-col items-center justify-center gap-3 pt-14 text-center"
    >
      <span class="text-heading-2 text-n-slate-12">
        {{ t('BAILEYS_CAMPAIGN.EMPTY_STATE.TITLE') }}
      </span>
      <p class="text-sm text-n-slate-11 max-w-sm">
        {{ t('BAILEYS_CAMPAIGN.EMPTY_STATE.SUBTITLE') }}
      </p>
    </div>

    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>
